import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Не из индекса фичи: индекс тянет урок и упражнения. Главной нужны только данные тем и правила наград.
import { isReadHere, readLastSlug } from '@features/Grammar/model/marks';
import { continueTarget, homeTopic, rewardCycle } from '@features/Grammar/model/path';
import { useGrammarTopicsQuery } from '@features/Grammar/model/queries';
import { PixelChestIcon } from '@features/Grammar/ui/PixelChestIcon';
import { trackEvent } from '@shared/analytics';
import { buildGrammarExercisePath, buildGrammarLessonPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, TokenIcon } from '@shared/ui';

/**
 * Грамматика — строка «Списка дня» (2026-11-03, вместо отдельной плашки): сколько тем до награды, тонкая шкала круга
 * и кнопка к следующей теме (та же, что «ты здесь» на тропинке, — `homeTopic`). Тем нет — строки нет.
 */
export function GrammarRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useGrammarTopicsQuery().data;
  const topics = data?.topics ?? [];
  const next = useMemo(() => homeTopic(topics, readLastSlug()), [topics]);
  if (!data || topics.length === 0) return null;

  const rewards = data.rewards;
  const cycle = rewards ? rewardCycle(rewards) : null;
  const title = !next ? t('dashboard.home.grammar.allDone') : cycle ? t('dashboard.home.grammar.left', { count: cycle.left }) : t('dashboard.home.grammar.plain');

  const open = () => {
    if (!next) {
      navigate(paths.grammar);
      return;
    }
    trackEvent('grammar_topic_opened', { slug: next.slug, place: 'home' });
    // Урок дочитан, а тема не пройдена — сразу в упражнение 1 (grammar-completion-tz § 3.4).
    navigate(
      continueTarget(next, isReadHere(next.slug)) === 'exercise' ? buildGrammarExercisePath(next.slug, 1) : buildGrammarLessonPath(next.slug),
    );
  };

  return (
    <div className="dl-row flex items-center gap-3">
      <span aria-hidden className="dl-art lx-px-sm grid h-12 w-12 flex-none place-items-center bg-[#2a2116]">
        <PixelChestIcon className="h-8 w-8" />
      </span>
      <div className="dl-text min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">
          {t('dashboard.home.grammar.kicker')}
          {cycle && next && rewards ? (
            <span className="inline-flex items-center gap-1 rounded-pill bg-[#f2c14e]/15 px-1.5 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-[#f2c14e]">
              <TokenIcon size={12} />
              {t('dashboard.home.grammar.reward', { count: rewards.step_tokens })}
            </span>
          ) : null}
        </div>
        <h3 className="mt-0.5 line-clamp-2 break-words font-serif text-[17px] font-medium leading-[22px] text-fg">{title}</h3>
        {cycle && next ? (
          <div className="mt-1.5 flex gap-1" aria-label={t('dashboard.home.grammar.ofStep', { done: cycle.done, step: cycle.step })}>
            {Array.from({ length: cycle.step }, (_, i) => (
              <span key={i} aria-hidden className={cn('h-1.5 flex-1 rounded-full', i < cycle.done ? 'bg-accent' : 'bg-white/[0.1]')} />
            ))}
          </div>
        ) : null}
      </div>
      <div className="dl-action flex-none">
        <Button variant="outline" size="sm" onClick={open}>
          {!next ? t('dashboard.home.grammar.all') : next.progress.opened ? t('dashboard.home.dailies.resume') : t('dashboard.home.dailies.start')}
        </Button>
      </div>
    </div>
  );
}
